import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getMe, logout as apiLogout, refreshOnce } from '../../lib/api';
import { getAccessToken, getAccessTokenExpiresAtMs, setAccessToken } from '../../lib/authToken';
import type { User } from '../../lib/types';

// Refresh this long before the access token actually expires — covers request
// latency and clock skew so a proactive refresh never races the real exp.
const REFRESH_LEAD_MS = 60_000;

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

type AuthContextValue = {
  user: User | null;
  status: AuthStatus;
  /** Re-fetch the current user from GET /api/auth/me. */
  reloadUser: () => Promise<void>;
  /** Sign out — clears the token and local user state. */
  signOut: () => Promise<void>;
  /** Called by LoginPage after a successful login response. */
  setUser: (user: User) => void;
  /**
   * Locally clears the session without calling /api/auth/logout — for cases
   * where the server has already ended the session itself (e.g. a
   * successful password change revokes all refresh tokens server-side).
   */
  clearSession: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  // Prevent double-hydration in StrictMode
  const hydrated = useRef(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  // Proactively refreshes ~60s before the in-memory access token expires, so
  // an idle tab never lets it lapse silently (idle = no requests to trigger
  // the reactive 401->refresh path in api.ts). Reschedules itself off the
  // freshly-issued token on success; on failure it just doesn't reschedule —
  // a single transient miss falls back to the existing reactive 401 retry,
  // it does NOT force a sign-out.
  const scheduleRefresh = useCallback(() => {
    clearRefreshTimer();
    const token = getAccessToken();
    const expiresAt = token ? getAccessTokenExpiresAtMs(token) : null;
    if (expiresAt === null) return;
    const delay = Math.max(expiresAt - Date.now() - REFRESH_LEAD_MS, 0);
    refreshTimer.current = setTimeout(() => {
      void refreshOnce().then((newToken) => {
        if (newToken) scheduleRefresh();
      });
    }, delay);
  }, [clearRefreshTimer]);

  const reloadUser = useCallback(async () => {
    try {
      const me = await getMe();
      setUserState(me);
      setStatus('authenticated');
      scheduleRefresh();
    } catch {
      setUserState(null);
      setAccessToken(null);
      setStatus('unauthenticated');
      clearRefreshTimer();
    }
  }, [scheduleRefresh, clearRefreshTimer]);

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    // Public link routes (/sign/:token, /onboard/:slug, D26's
    // /forgot-password, /reset-password, S50's /certificate) have no session and must never
    // trigger the getMe() -> refresh -> redirectToLogin() bootstrap chain —
    // that's what was hard-navigating signed-out vendors/reps off these
    // public links to /login.
    //
    // /login is NOT skip-listed: a hard reload while already authenticated
    // must still bootstrap so LoginPage's status==='authenticated' redirect
    // can fire (otherwise it only worked on SPA nav, never on a fresh load).
    // This is safe because api.ts's redirectToLogin() no-ops when already on
    // /login, so a genuinely signed-out user just gets one failed getMe() ->
    // unauthenticated, same as today.
    const path = window.location.pathname;
    if (
      path.startsWith('/sign') ||
      path.startsWith('/onboard') ||
      path.startsWith('/forgot-password') ||
      path.startsWith('/reset-password') ||
      path.startsWith('/certificate')
    ) {
      setStatus('unauthenticated');
      return;
    }
    void reloadUser();
  }, [reloadUser]);

  // Belt-and-suspenders: clear the timer on unmount too (StrictMode/HMR).
  useEffect(() => clearRefreshTimer, [clearRefreshTimer]);

  const signOut = useCallback(async () => {
    clearRefreshTimer();
    await apiLogout();
    setUserState(null);
    setStatus('unauthenticated');
  }, [clearRefreshTimer]);

  const setUser = useCallback(
    (u: User) => {
      setUserState(u);
      setStatus('authenticated');
      scheduleRefresh();
    },
    [scheduleRefresh],
  );

  const clearSession = useCallback(() => {
    clearRefreshTimer();
    setAccessToken(null);
    setUserState(null);
    setStatus('unauthenticated');
  }, [clearRefreshTimer]);

  return (
    <AuthContext.Provider value={{ user, status, reloadUser, signOut, setUser, clearSession }}>
      {children}
    </AuthContext.Provider>
  );
}
